import React, {useState} from 'react';
import {ClickAwayListener} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import useContentSubmissionsStore, {
  contentSubmissionsActions,
} from '@/stores/content/content-submissions/contentSubmissionsStore';
import type {CampaignCreator} from '../../types/campaignCreator';
import CreatorAutocomplete, {
  type CreatorChangeReason,
} from '../creator-autocomplete';
import styles from './CreatorsFilter.module.scss';

type CreatorsFilterProps = {
  testID?: string;
  campaignCreators: CampaignCreator[];
  onCreatorSelected?: () => void;
};

function CreatorsFilter({
  testID = 'creators-filter',
  campaignCreators,
  onCreatorSelected,
}: CreatorsFilterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedCreator = useContentSubmissionsStore(
    (state) => state.appliedFilters.talent
  );

  const buttonLabel = selectedCreator?.name ?? translate('All Creators');

  const handleChange = (
    creator: CampaignCreator | null,
    reason: CreatorChangeReason
  ) => {
    // Backspacing over the pre-filled name means the user is typing a new one —
    // keep the dropdown open and leave the applied filter alone until they pick
    // a creator or press the clear button.
    if (reason === 'searchTextEmptied') {
      return;
    }

    contentSubmissionsActions.setAndApplyTalent(creator);
    onCreatorSelected?.();
    setIsOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div className={styles.container}>
        <Button
          testID={testID}
          variant="text"
          size="small"
          endIcon={
            <Icon
              size="small"
              appearance="neutral"
              name="Actions-Arrow-Down-Small"
              color={designSystemToken('semantic.fg.secondary')}
              style={{width: '18px', height: '18px'}}
            />
          }
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
            {buttonLabel}
          </Body>
        </Button>

        {isOpen && (
          <div className={styles.dropdown}>
            <CreatorAutocomplete
              testID={testID}
              campaignCreators={campaignCreators}
              selectedCreator={selectedCreator}
              onChange={handleChange}
              openOnFocus
              autoFocus
              selectTextOnFocus
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

export default CreatorsFilter;
